/**
 * Lifecycle states of an Order of Service.
 * CreateOrder inicia en PRELIMINAR (o CONFIRMADO si asOfficial); Financials gestiona el resto.
 */
export enum OrderStatus {
  PRELIMINAR = 'PRELIMINAR',
  CONFIRMADO = 'CONFIRMADO',
  EN_PROCESO = 'EN PROCESO',
  PROCESADO = 'PROCESADO',
  ELIMINADO = 'ELIMINADO',
}

/** P-{n} mientras es preliminar; si no, el número normal. */
export function formatOrderDisplayCode(
  orderNumber?: string | number | null,
  orderStatus?: string | null,
): string {
  const number = String(orderNumber ?? '').trim();
  if (!number) {
    return '';
  }
  return orderStatus === OrderStatus.PRELIMINAR ? `P-${number}` : number;
}

export function isOrderStatusPreliminary(
  orderStatusString: string | null | undefined,
): boolean {
  return orderStatusString?.trim().toUpperCase() === OrderStatus.PRELIMINAR;
}

/**
 * Reservation states of an Order of Service.
 */
export enum OrderReservationStatus {
  EN_ESPERA = 'EN ESPERA',
  CONFIRMADO = 'CONFIRMADO',
  RECHAZADO = 'RECHAZADO',
  ELIMINADO = 'ELIMINADO',
}

/**
 * Technical generation states of an Order PDF document.
 */
export enum OrderPdfStatus {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  GENERATED = 'GENERATED',
  FAILED = 'FAILED',
}

/**
 * Normalizes a raw PDF status string to an OrderPdfStatus enum value.
 *
 * Description:
 *   Converts string to uppercase and matches against OrderPdfStatus enum values.
 *
 * Parameters:
 *   pdfStatusString: string | null | undefined - The raw PDF status string.
 *
 * Return Value:
 *   OrderPdfStatus | null - Normalized enum value or null if unrecognized.
 *
 * Exceptions & Errors:
 *   None.
 */
export function normalizePdfStatus(
  pdfStatusString: string | null | undefined,
): OrderPdfStatus | null {
  const normalizedString = pdfStatusString?.trim().toUpperCase();
  if (!normalizedString) {
    return null;
  }

  if (
    Object.values(OrderPdfStatus).includes(normalizedString as OrderPdfStatus)
  ) {
    return normalizedString as OrderPdfStatus;
  }

  return null;
}

/**
 * Resolves the effective PDF status from an order record.
 *
 * Description:
 *   Checks explicit pdfStatus field or infers PENDING if an active order exists.
 *
 * Parameters:
 *   orderRecord: { pdfStatus?: string | null; orderNumber?: string | null; orderId?: string | null } | null | undefined - Order payload.
 *
 * Return Value:
 *   OrderPdfStatus | null - Inferred or explicit status.
 *
 * Exceptions & Errors:
 *   None.
 */
export function resolveOrderPdfStatus(
  orderRecord?: {
    pdfStatus?: string | null;
    orderNumber?: string | number | null;
    orderId?: string | null;
  } | null,
): OrderPdfStatus | null {
  const normalizedExplicitStatus = normalizePdfStatus(
    orderRecord?.pdfStatus ?? null,
  );
  if (normalizedExplicitStatus) {
    return normalizedExplicitStatus;
  }

  if (orderRecord?.orderId || orderRecord?.orderNumber) {
    return OrderPdfStatus.PENDING;
  }

  return null;
}

/**
 * Checks whether the PDF is actively generating (PENDING or PROCESSING).
 *
 * Description:
 *   Evaluates resolveOrderPdfStatus against PENDING and PROCESSING states.
 *
 * Parameters:
 *   pdfStatusString: string | null | undefined - Status string to evaluate.
 *
 * Return Value:
 *   boolean - True if currently generating.
 *
 * Exceptions & Errors:
 *   None.
 */
export function isPdfGenerating(
  pdfStatusString: string | null | undefined,
): boolean {
  const resolvedStatus = resolveOrderPdfStatus({
    pdfStatus: pdfStatusString,
  });
  return (
    resolvedStatus === OrderPdfStatus.PENDING ||
    resolvedStatus === OrderPdfStatus.PROCESSING
  );
}

/**
 * Checks whether the PDF is generated and ready for viewing/download.
 *
 * Description:
 *   Compares resolved PDF status against GENERATED.
 *
 * Parameters:
 *   pdfStatusString: string | null | undefined - Status string to evaluate.
 *
 * Return Value:
 *   boolean - True if GENERATED.
 *
 * Exceptions & Errors:
 *   None.
 */
export function isPdfReady(
  pdfStatusString: string | null | undefined,
): boolean {
  return (
    resolveOrderPdfStatus({ pdfStatus: pdfStatusString }) ===
    OrderPdfStatus.GENERATED
  );
}

/**
 * Checks whether the PDF generation has failed.
 *
 * Description:
 *   Compares resolved PDF status against FAILED.
 *
 * Parameters:
 *   pdfStatusString: string | null | undefined - Status string to evaluate.
 *
 * Return Value:
 *   boolean - True if FAILED.
 *
 * Exceptions & Errors:
 *   None.
 */
export function isPdfFailed(
  pdfStatusString: string | null | undefined,
): boolean {
  return (
    resolveOrderPdfStatus({ pdfStatus: pdfStatusString }) ===
    OrderPdfStatus.FAILED
  );
}

/**
 * Evaluates whether an order's status is CONFIRMADO.
 *
 * Description:
 *   Checks if status matches OrderStatus.CONFIRMADO.
 *
 * Parameters:
 *   orderStatusString: string | null | undefined - Order status string.
 *
 * Return Value:
 *   boolean - True if CONFIRMADO.
 *
 * Exceptions & Errors:
 *   None.
 */
export function isOrderStatusConfirmed(
  orderStatusString: string | null | undefined,
): boolean {
  return orderStatusString?.trim().toUpperCase() === OrderStatus.CONFIRMADO;
}

/**
 * Evaluates whether an Order of Service can be deleted.
 *
 * Description:
 *   Orders can only be logically deleted when in CONFIRMADO status.
 *
 * Parameters:
 *   orderStatusString: string | null | undefined - The status of the order.
 *
 * Return Value:
 *   boolean - True if deletable (CONFIRMADO); false otherwise.
 *
 * Exceptions & Errors:
 *   None.
 */
export function isOrderStatusDeletable(
  orderStatusString: string | null | undefined,
): boolean {
  return (
    isOrderStatusConfirmed(orderStatusString) ||
    isOrderStatusPreliminary(orderStatusString)
  );
}

export function isOrderEditable(serviceRecord: {
  order?: { orderId?: string | null; orderStatus?: string | null } | null;
}): boolean {
  const status = serviceRecord?.order?.orderStatus;
  return (
    Boolean(serviceRecord?.order?.orderId) &&
    (isOrderStatusConfirmed(status) || isOrderStatusPreliminary(status))
  );
}

/**
 * Checks whether a service item allows opening its PDF preview.
 * PRELIMINAR no muestra PDF (aún no oficial / NOT_APPLICABLE).
 */
export function canShowOrderPdf(serviceRecord: {
  order?: {
    orderId?: string | null;
    orderNumber?: string | number | null;
    orderStatus?: string | null;
    pdfStatus?: string | null;
  } | null;
  orderId?: string | null;
}): boolean {
  if (isOrderStatusPreliminary(serviceRecord?.order?.orderStatus)) {
    return false;
  }
  if (serviceRecord?.order?.pdfStatus === OrderPdfStatus.NOT_APPLICABLE) {
    return false;
  }
  return (
    Boolean(serviceRecord?.order?.orderId) ||
    Boolean(serviceRecord?.order?.orderNumber) ||
    Boolean(serviceRecord?.orderId)
  );
}

/**
 * Generates an informative blocking message explaining why an order cannot be mutated.
 *
 * Description:
 *   Returns custom messages for PROCESADO, EN PROCESO, ELIMINADO, etc.
 *
 * Parameters:
 *   actionTypeDescription: 'eliminar' | 'editar' - The attempted action.
 *   orderStatusString: string | null | undefined - The current status of the order.
 *
 * Return Value:
 *   string - Explanation message for the user.
 *
 * Exceptions & Errors:
 *   None.
 */
export function getOrderMutationBlockedMessage(
  actionTypeDescription: 'eliminar' | 'editar',
  orderStatusString: string | null | undefined,
): string {
  const normalizedStatus = orderStatusString?.trim().toUpperCase();

  if (normalizedStatus === OrderStatus.PROCESADO) {
    return `No se puede ${actionTypeDescription} la Orden de Servicio porque ya fue PROCESADA por Tesorería.`;
  }

  if (normalizedStatus === OrderStatus.EN_PROCESO) {
    return `No se puede ${actionTypeDescription} la Orden de Servicio porque se encuentra EN PROCESO de pago.`;
  }

  if (normalizedStatus === OrderStatus.ELIMINADO) {
    return `Esta Orden de Servicio ya se encuentra ELIMINADA.`;
  }

  if (actionTypeDescription === 'eliminar') {
    return `Solo se pueden eliminar Órdenes de Servicio en estado CONFIRMADO o PRELIMINAR (estado actual: ${normalizedStatus || 'desconocido'}).`;
  }

  return `Solo se puede editar una OS confirmada o preliminar (estado actual: ${normalizedStatus || 'desconocido'}).`;
}
